
  import React, { useState, useEffect } from 'react';
  import { motion, useScroll, useSpring } from 'framer-motion';
  import Navbar from './components/Navbar';
  import Header from './components/Header';
  import About from './components/About';
  import Experience from './components/Experience';
  import TechStack from './components/TechStack';
  import Projects from './components/Projects';
  import Certifications from './components/Certifications';
  import Recommendations from './components/Recommendations';
  import Gallery from './components/Gallery';
  import Footer from './components/Footer';
  import ParticleBackground from './components/ParticleBackground';

  const App: React.FC = () => {
    const [darkMode, setDarkMode] = useState(true);
    const { scrollYProgress } = useScroll();
    const scaleX = useSpring(scrollYProgress, {
      stiffness: 100,
      damping: 30,
      restDelta: 0.001
    });

    useEffect(() => {
      if (darkMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }, [darkMode]);

    const toggleTheme = () => setDarkMode(!darkMode);

  const SectionWrapper = ({ children, id }: { children: React.ReactNode, id: string }) => (
  <motion.div
    id={id}
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.8, ease: "easeOut" }}
    className="relative z-10" // Ensures content stays above background
  >
    {children}
  </motion.div>
);

return (
  <div className={`min-h-screen relative overflow-hidden ${
    darkMode ? 'bg-slate-950 text-white transition-colors duration-500' : 'bg-slate-50 text-slate-900'
  }`}>
    <ParticleBackground darkMode={darkMode} />

    <div className="fixed inset-0 z-0 pointer-events-none overflow-hidden">
      <div className={`absolute top-0 left-0 w-full h-full opacity-30 blur-[100px] ${darkMode ? 'bg-blue-900/20' : 'bg-blue-100/50'}`} />
    </div>
    
    <div className="fixed inset-0 z-0 pointer-events-none">
      <motion.div 
        animate={{
          x: [0, 100, 0],
          y: [0, 50, 0],
          scale: [1, 1.2, 1],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        className={`absolute -top-20 -left-20 w-96 h-96 rounded-full blur-[120px] opacity-20 
          ${darkMode ? 'bg-blue-600' : 'bg-blue-100'}`}
      />
      
      <motion.div 
        animate={{
          x: [0, -100, 0],
          y: [0, 150, 0],
          scale: [1, 1.5, 1],
        }}
        transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
        className={`absolute top-1/2 -right-20 w-[500px] h-[500px] rounded-full blur-[150px] opacity-10 
          ${darkMode ? 'bg-indigo-500' : 'bg-indigo-300'}`}
      />
    </div>
    {/* ------------------------------------- */}

    {/* Scroll Progress Bar */}
    <motion.div
      className="fixed top-0 left-0 right-0 h-1 z-[100] origin-left"
      style={{ scaleX }}
      animate={{
        background: darkMode 
          ? 'linear-gradient(to right, #2563eb, #4f46e5)' 
          : 'linear-gradient(to right, #3b82f6, #6366f1)'
      }}
      transition={{ duration: 0.5 }}
    />
        
        <Navbar darkMode={darkMode} toggleTheme={toggleTheme} />
        
        <main className="space-y-24 pb-20 overflow-hidden">
          <div id="home">
            <Header />
          </div>

          <div className="container mx-auto px-4 md:px-8 space-y-48">
            <SectionWrapper id="about">
              <About darkMode={darkMode} />
            </SectionWrapper>

            <SectionWrapper id="experience">
              <Experience darkMode={darkMode} />
            </SectionWrapper>

            <SectionWrapper id="tech-stack">
              <TechStack darkMode={darkMode} />
            </SectionWrapper>

            <SectionWrapper id="projects">
              <Projects darkMode={darkMode} />
            </SectionWrapper>

            <SectionWrapper id="certifications">
              <Certifications darkMode={darkMode} />
            </SectionWrapper>

            <SectionWrapper id="recommendations">
              <Recommendations darkMode={darkMode} />
            </SectionWrapper>
          </div>
        </main>

        <Footer />
      </div>
    );
  };

  export default App;
